/**
 * 统一音频播放逻辑 - 基于 Howler.js
 * 适用于 PC 端和移动端，以移动端的成熟实现为基础
 */

import type { RootState } from "../state";
import { emitter } from "@/utils/emitter/mitt";
import { HowlerSeamlessPlayer, type HowlerAudioItem } from "./howlerAudio";
import { Howl } from "howler";
import { augmentStoreWithAppleHiddenWebAudio } from "./appleBackgroundWebAudio";
import {
  ensureUniversalWebAudio,
  feedUniversalWebAudio
} from "./universalBackgroundWebAudio";
import { getConfig } from "@/config";

// 平台检测和WebAudio分支选择逻辑
function shouldUseUniversalWebAudio(roomId: string): boolean {
  try {
    const ua = navigator.userAgent || "";

    // 检测是否为PC Chrome
    const isPC = !/iPhone|iPad|iPod|Android/i.test(ua);
    const isChrome = /Chrome/.test(ua) && !/Edge/.test(ua);
    const isPCChrome = isPC && isChrome;

    // 检测是否为Android Chrome
    const isAndroid = /Android/i.test(ua);
    const isAndroidChrome = isAndroid && isChrome;

    // 检查全局配置开关
    const g: any = globalThis as any;
    const config = typeof getConfig === "function" ? getConfig() : {};
    const enableUniversalWebAudio =
      (config as any)?.enableUniversalWebAudio === true;

    // PC Chrome 或 Android Chrome 且开启了配置开关则使用 UniversalWebAudio
    const shouldUse =
      (isPCChrome || isAndroidChrome) && enableUniversalWebAudio;

    if (shouldUse) {
      console.log(
        `🎵 [UniversalWebAudio] 检测到${isPCChrome ? "PC" : "Android"} Chrome，启用 UniversalWebAudio 播放器 room=${roomId}`
      );

      // 标记房间使用 UniversalWebAudio（类似 iOS 的 __appleUseWAOnlyRooms）
      if (!g.__universalWebAudioRooms) {
        g.__universalWebAudioRooms = new Set();
      }
      g.__universalWebAudioRooms.add(roomId);
    }

    return shouldUse;
  } catch (e) {
    console.warn(`🚫 平台检测失败:`, e);
    return false;
  }
}

// 统一音频播放主函数
export async function playNext(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  if (!roomId || !this.roomStates.has(roomId)) {
    console.error(`🚫 音频播放: 房间${roomId}不存在`);
    return false;
  }

  const state = this.roomStates.get(roomId);

  // 修复防抖逻辑：减少防抖时间，避免跳过正常的产品切换播放
  const now = Date.now();
  const playKey = `${roomId}_play_next`;
  if (!state.lastLogTime) state.lastLogTime = {};

  // 关键修复：产品切换时不应用防抖，允许立即播放新产品音频
  const isProductSwitch =
    state.pendingProductSwitch ||
    (state.lastProductSwitchTime && now - state.lastProductSwitchTime < 1000);

  if (
    !isProductSwitch &&
    state.lastLogTime[playKey] &&
    now - state.lastLogTime[playKey] < 200
  ) {
    console.warn(
      `🎵 音频播放防抖：跳过重复调用 (${now - state.lastLogTime[playKey]}ms)`
    );
    return false;
  }
  state.lastLogTime[playKey] = now;

  // 记录产品切换时间，用于防抖判断
  if (isProductSwitch) {
    console.log(`🎵 检测到产品切换，跳过防抖逻辑，立即播放`);
  }

  // 获取下一个音频
  const nextAudio = this.getNextAudioFromQueue(roomId);
  if (!nextAudio) {
    console.warn(`🚫 音频播放: 房间${roomId}队列为空`);
    state.isPlaying = false;
    return false;
  }

  // 转换为兼容格式
  const item = {
    id: nextAudio.id,
    text: nextAudio.text,
    audioUrl: nextAudio.audioUrl,
    playedCharCount: nextAudio.playedCharCount || 0,
    productId: (nextAudio as any).productId || null,
    commentId: (nextAudio as any).commentId || null
  };

  if (!item.audioUrl) {
    // 尝试播放下一个
    const queueLength = this.getQueueLength(roomId);
    if (queueLength > 0) {
      console.warn(`🎵 当前音频无URL，尝试下一个 (队列剩余: ${queueLength})`);
      // 不使用固定延迟，直接尝试下一条
      this.playNext(roomId);
    } else {
      state.isPlaying = false;
    }
    return false;
  }

  state.isPlaying = true;

  try {
    return await playWithHowler.call(this, roomId, item);
  } catch (error) {
    console.error("🎵 Howler播放失败:", error);
    state.isPlaying = false;
    return false;
  }
}

// 统一 Howler 播放器处理
export async function playWithHowler(
  this: RootState & { [key: string]: any },
  roomId: string,
  item: any
) {
  const state = this.roomStates.get(roomId);
  if (!state) return false;

  // 🎵 UniversalWebAudio 分支检测（优先级最高）
  const g: any = globalThis as any;
  const useUniversalWebAudio = !!(
    g &&
    g.__universalWebAudioRooms &&
    typeof g.__universalWebAudioRooms.has === "function" &&
    g.__universalWebAudioRooms.has(roomId)
  );

  if (useUniversalWebAudio) {
    // console.log(`🎵 [UniversalWebAudio] 使用UniversalWebAudio播放分支`);

    // 确保播放器存在，如果不存在则创建
    await ensureUniversalWebAudio.call(this as any, roomId);
    const player = (state as any).universalWebAudioPlayer;
    if (
      player.player.playlist.length &&
      player.player.playlist[player.player.playlist.length - 1].id == item.id
    ) {
      console.warn(`🎵 [UniversalWebAudio] 检测到重复添加 id=${item.id}`);
      return true;
    }
    if (!player) {
      state.isPlaying = false;
      return false;
    }

    try {
      const skipId = (state as any).__skipUniversalPlayId || null;
      const status = player.getStatus?.();
      const lastId = (state as any).__universalCurrentId || null;

      if (skipId && skipId === item.id) {
        try {
          console.log(
            `🎵 [UniversalWebAudio] 检测到已跳过标记，完全跳过重复处理 id=${item.id}`
          );
        } catch {}

        // 无论播放器是否在播放，都清除标记并返回
        (state as any).__skipUniversalPlayId = null;
        (state as any).__universalCurrentId = item.id;
        (state as any).__universalCurrentStartedAt = Date.now();
        state.isPlaying = true;
        return true;
      }

      // 仅在特定条件下检测重复播放：页面刷新后首次加载场景
      // 避免影响socket正常传递的重复音频需求
      let duplicateDetected = false;
      if (
        status?.isPlaying &&
        lastId &&
        lastId === item.id &&
        // 添加时间阈值检查，只在短时间内的重复请求才跳过
        (state as any).__universalCurrentStartedAt &&
        Date.now() - (state as any).__universalCurrentStartedAt < 1000
      ) {
        duplicateDetected = true;
      }

      if (duplicateDetected) {
        try {
          console.warn(
            `🎵 [UniversalWebAudio] 检测到短时间内重复播放请求，忽略 id=${item.id}`
          );
        } catch {}
        return true;
      }

      // logPlaybackStart();

      await player.addToPlaylist({
        id: item.id,
        text: item.text || "",
        audioUrl: item.audioUrl,
        productId: (item as any).productId,
        voiceNo: (item as any).voiceNo,
        commentId: (item as any).commentId
      });

      if (!player.getStatus().isPlaying) {
        await player.play();
        state.isPlaying = true;

        // 发送播放开始事件
        // try {
        //   emitter.emit("audioStart", {
        //     roomId,
        //     audioId: item.id,
        //     text: item.text,
        //     player: "universalWebAudio"
        //   });
        // } catch {}
      }

      console.log(
        `🎵 [UniversalWebAudio] 音频已添加到播放列表: "${item.text.substring(0, 30)}..."`
      );

      try {
        const queueIdx = state.currentIndex;
        const queueItem = state.queue?.[queueIdx];
        if (queueItem && queueItem.id === item.id) {
          queueItem.status = "playing";
          const cloned = [...state.queue];
          cloned[queueIdx] = { ...queueItem } as any;
          state.queue = cloned;
        }
        (state as any).__universalCurrentId = item.id;
        (state as any).__universalCurrentStartedAt = Date.now();
        (state as any).__skipUniversalPlayId = null;
      } catch {}
      return true;
    } catch (error) {
      console.error(`🚫 [UniversalWebAudio] 播放失败:`, error);
      state.isPlaying = false;
      return false;
    }
  }

  // 🍎/🎛️ WebAudio 优先调度：
  // - 苹果端默认走 WebAudio（前后台一致，不在段间触发 Howler play()）
  // - 或当配置指定 Backend=WebAudio（全端强制使用 WebAudio）
  try {
    const ua = navigator.userAgent || "";
    const appleLike =
      /iPhone|iPad|iPod/i.test(ua) ||
      (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1) ||
      (/Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua));
    const cfg: any =
      (typeof getConfig === "function" ? getConfig() : {}) || ({} as any);
    const forceWA =
      String(cfg?.Audio?.Backend || "").toLowerCase() === "webaudio" ||
      Boolean(cfg?.Audio?.ForceWebAudioForAll);
    if (appleLike || forceWA) {
      // 挂载 WA 分支能力
      try {
        augmentStoreWithAppleHiddenWebAudio(this as any);
      } catch {}
      // 确保为房间创建/获取 WA 播放器
      try {
        (this as any).ensureAppleHiddenWebAudio &&
          (this as any).ensureAppleHiddenWebAudio(roomId);
      } catch {}
      // 投喂下一条（即可见/隐藏统一调度，段间不调用 play）
      try {
        (this as any).feedAppleHiddenWebAudio &&
          (this as any).feedAppleHiddenWebAudio(roomId, {
            id: item.id,
            text: item.text,
            audioUrl: item.audioUrl,
            productId: (item as any).productId ?? null
          });
      } catch {}
      // 标记在播，元数据尽早设置（用于 Now Playing）
      state.isPlaying = true;
      try {
        this.setupMediaSession && this.setupMediaSession(item);
      } catch {}
      try {
        emitter.emit("productAudioStarted", {
          roomId,
          productId:
            (item as any).productId ??
            state.switchingProductId ??
            state.selectedProductId ??
            null,
          audioId: item.id,
          source: "apple-wa"
        });
      } catch {}
      return true;
    }
  } catch {}

  // 初始化播放器（支持UniversalWebAudio分支）
  const shouldUseUniversalWA = shouldUseUniversalWebAudio(roomId);

  if (shouldUseUniversalWA) {
    // 使用 UniversalWebAudio 播放器
    await ensureUniversalWebAudio.call(this as any, roomId);
    const player = (state as any).universalWebAudioPlayer;
    if (
      player.player.playlist.length &&
      player.player.playlist[player.player.playlist.length - 1].id == item.id
    ) {
      console.warn(`🎵 [UniversalWebAudio] 检测到重复添加 id=${item.id}`);
      return true;
    }
    try {
      await player.addToPlaylist({
        id: item.id,
        text: item.text || "",
        audioUrl: item.audioUrl,
        productId: state.selectedProductId,
        voiceNo: (item as any).voiceNo,
        commentId: (item as any).commentId
      });

      if (!player.getStatus().isPlaying) {
        await player.play();
        state.isPlaying = true;
        // emitter.emit("audioStart", {
        //   roomId,
        //   audioId: item.id,
        //   text: item.text,
        //   player: "universalWebAudio"
        // });
      }

      console.log(
        `🎵 [UniversalWebAudio] 播放音频: "${item.text.substring(0, 30)}..."`
      );
      return true;
    } catch (error) {
      console.error(`🚫 [UniversalWebAudio] 播放失败:`, error);
      this.onPlayError(roomId, error);
      return false;
    }
  }

  // 初始化Howler播放器（非苹果端，非UniversalWebAudio）
  if (!(state as any).howlerPlayer) {
    const player = new HowlerSeamlessPlayer(roomId);

    // 设置进度回调
    player.setProgressCallback((_howlerIndex: number, progress: number) => {
      // 修复：允许所有房间更新自己的进度，但只有当前房间更新UI
      const isCurrentRoom = this.currentRoomId === roomId;

      if (!isCurrentRoom) {
        // 后台房间仍需要更新进度，只是不更新UI状态
        console.log(
          `🔇 房间${roomId}后台进度更新: ${(progress * 100).toFixed(1)}%`
        );
      }

      // 关键修复：直接更新当前队列索引对应的音频
      const currentItem = state.queue[state.currentIndex];
      if (currentItem) {
        // 保存真实的音频播放进度
        (currentItem as any).audioTimeProgress = progress;

        const totalChars = currentItem.text.length;
        const newPlayedCharCount = Math.floor(progress * totalChars);

        // 更新文字显示进度
        currentItem.playedCharCount = newPlayedCharCount;
        currentItem.status = "playing";

        // 强制触发Vue响应式更新
        const newQueue = [...state.queue];
        newQueue[state.currentIndex] = { ...currentItem };
        state.queue = newQueue;

        // 定期保存状态（每10%进度保存一次）
        if (
          Math.floor(progress * 10) >
          Math.floor(((currentItem as any).lastSavedProgress || 0) * 10)
        ) {
          try {
            (currentItem as any).lastSavedProgress = progress;
            // console.log(`🎵 自动保存音频进度: ${(progress * 100).toFixed(1)}%`);
            this.saveStateToStorage();
          } catch {
            // 忽略保存错误
          }
        }

        // 添加调试信息（降低频率）
        if (newPlayedCharCount % 10 === 0) {
          // console.log(
          //   `🎵 文字显示进度: ${newPlayedCharCount}/${totalChars} (${(progress * 100).toFixed(1)}%) - "${currentItem.text.substring(0, 30)}..."`
          // );
        }

        // 定期保存状态（每10%进度保存一次）
        if (
          Math.floor(progress * 10) >
          Math.floor(((currentItem as any).lastSavedProgress || 0) * 10)
        ) {
          try {
            (currentItem as any).lastSavedProgress = progress;
            // console.log(`🎵 自动保存音频进度: ${(progress * 100).toFixed(1)}%`);
            this.saveStateToStorage();
            // 诊断：输出 UI 端将使用的进度来源（audioTimeProgress vs playedCharCount）及 Howler 端的时间进度
            try {
              const howlerStatus = (state as any).howlerPlayer?.getStatus?.();
              const audioPct = howlerStatus?.duration
                ? Math.round(
                    (howlerStatus.currentTime / howlerStatus.duration) * 100
                  )
                : 0;
              const textLen = currentItem.text.length || 1;
              const textPct =
                typeof (currentItem as any).audioTimeProgress === "number" &&
                (currentItem as any).audioTimeProgress >= 0
                  ? Math.round((currentItem as any).audioTimeProgress * 100)
                  : Math.round(
                      ((currentItem.playedCharCount || 0) / textLen) * 100
                    );
              console.log(
                `[probe] UI_PROGRESS_SOURCE room=${roomId} idx=${state.currentIndex}/${state.queue.length} id=${currentItem.id} audioPct=${audioPct}% textPct=${textPct}% src=${typeof (currentItem as any).audioTimeProgress === "number" ? "time" : "chars"}`
              );
            } catch {}
          } catch {
            // 忽略保存错误
          }
        }

        // 添加调试信息（降低频率）
        if (newPlayedCharCount % 10 === 0) {
          // console.log(
          //   `🎵 文字显示进度: ${newPlayedCharCount}/${totalChars} (${(progress * 100).toFixed(1)}%) - "${currentItem.text.substring(0, 30)}..."`
          // );
        }
      }

      // 更新媒体会话的播放位置（支持的浏览器）
      try {
        if (
          "mediaSession" in navigator &&
          (navigator as any).mediaSession.setPositionState
        ) {
          const howlerStatus = (state as any).howlerPlayer.getStatus();
          // @ts-ignore
          (navigator as any).mediaSession.setPositionState({
            duration: howlerStatus.duration || 0,
            playbackRate: 1,
            position: howlerStatus.currentTime || 0
          });
        }
      } catch {}

      // 保持字幕与实际播放项ID一致：在进度回调低频同步
      try {
        if (typeof (this as any).reconcileSubtitleWithAudible === "function") {
          (this as any).reconcileSubtitleWithAudible(roomId);
        }
      } catch {}

      // 进入“边界窗口”后再启动兜底：仅当存在待切换，且接近尾声时才安排短兜底
      try {
        if (state.pendingProductSwitch) {
          const scheduled = !!(state as any)._pendingBoundaryTimerScheduled;
          // 接近尾声阈值：98%
          if (!scheduled && progress >= 0.98) {
            (state as any)._pendingBoundaryTimerScheduled = true;
            (state as any)._pendingBoundaryTimerId = setTimeout(() => {
              try {
                const st = this.roomStates.get(roomId);
                if (st && st.pendingProductSwitch) {
                  console.log("[switch-fallback] 边界窗口超时，强制执行待切换");
                  // 使用 Web 版本兜底执行，避免依赖 oncomplete
                  if (
                    typeof (this as any).executePendingProductSwitchWeb ===
                    "function"
                  ) {
                    (this as any).executePendingProductSwitchWeb(roomId);
                  }
                }
              } catch {}
              try {
                (state as any)._pendingBoundaryTimerScheduled = false;
                (state as any)._pendingBoundaryTimerId = null;
              } catch {}
            }, 2000);
            console.log("[switch-fallback] 进入边界窗口，安排2秒兜底");
          }
        }
      } catch {}
    });

    // 设置完成回调
    player.setCompleteCallback((_howlerIndex: number) => {
      console.log(
        `🎵 Howler音频播放完成: howlerIndex=${_howlerIndex}, 主队列当前索引=${state.currentIndex}, 当前房间=${this.currentRoomId}`
      );

      try {
        const hidden =
          typeof document !== "undefined" ? document.hidden : false;
        const hasGuard =
          typeof (globalThis as any).shouldBlockHowlerInAppleHidden ===
          "function";
        const guardDecision = hasGuard
          ? (globalThis as any).shouldBlockHowlerInAppleHidden()
          : "absent";
        console.warn(
          `[probe] COMPLETE_CB_PLAYNEXT_TRIGGER hidden=${hidden} guard=${guardDecision}`
        );
      } catch {}

      // 修复：允许所有房间处理完成回调，但只有当前房间触发下一首
      const isCurrentRoom = this.currentRoomId === roomId;
      if (!isCurrentRoom) {
        console.warn(
          `🔇 音频完成回调: 房间${roomId}不是当前房间(${this.currentRoomId})，后台继续播放下一首`
        );
        // 后台房间也需要继续播放下一首
        setTimeout(() => {
          this.playNext(roomId);
        }, 50);
        return;
      }

      // 关键修复：先处理当前音频的完成状态
      const completedMainIndex = state.currentIndex;
      if (state.queue[completedMainIndex]) {
        state.queue[completedMainIndex].status = "played";
        state.queue[completedMainIndex].playedCharCount =
          state.queue[completedMainIndex].text.length;
        console.log(
          `🎵 标记音频${completedMainIndex}为已播放: "${state.queue[completedMainIndex].text.substring(0, 30)}..."`
        );
      }

      // 关键修复：推进到下一个音频
      state.currentIndex++;
      console.log(`🎵 主队列索引推进到: ${state.currentIndex}`);

      // 新增：检查是否有待切换的产品
      if (state.pendingProductSwitch) {
        // 清理“边界窗口兜底”计时器（已到达边界，正常执行）
        try {
          if ((state as any)._pendingBoundaryTimerId) {
            clearTimeout((state as any)._pendingBoundaryTimerId);
            (state as any)._pendingBoundaryTimerId = null;
            (state as any)._pendingBoundaryTimerScheduled = false;
          }
        } catch {}
        console.log(
          `🔄 检测到待切换产品，执行产品切换: ${state.pendingProductSwitch.productId}`
        );
        console.log(`🔍 待切换产品详情:`, state.pendingProductSwitch);

        // 修复：先停止播放状态，再执行产品切换
        state.isPlaying = false;

        // 执行产品切换 - 使用正确的函数调用
        this.executePendingProductSwitch(roomId);
        return; // 不继续播放，等待产品切换完成
      } else {
        console.log(`🔍 当前没有待切换的产品，继续正常播放流程`);
      }

      // 检查是否需要请求新音频
      const howlerStatus = player.getStatus();
      console.log(
        `🎵 Howler播放器状态: 当前${howlerStatus.currentIndex}/${howlerStatus.totalItems}, 队列剩余: ${state.queue.length - state.currentIndex}`
      );

      // 关键修复：检查是否还有音频需要播放（只有在没有待切换产品时才继续播放）
      if (
        state.currentIndex < state.queue.length &&
        !state.pendingProductSwitch
      ) {
        // 确保只有当前房间才继续播放
        if (this.currentRoomId === roomId) {
          console.log(
            `🎵 继续播放下一个音频 (${state.currentIndex}/${state.queue.length})`
          );

          // 修复：iOS/Safari 隐藏态下由 WebAudio 接管，统一层不再触发 Howler 的下一条
          try {
            const hidden =
              typeof document !== "undefined" ? document.hidden : false;
            const ua = navigator.userAgent || "";
            const isIOS =
              /iPhone|iPad|iPod/i.test(ua) ||
              (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
            const isSafari =
              /Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua);
            if (hidden && (isIOS || isSafari)) {
              console.warn("[fix] COMPLETE_CB_SKIP_PLAYNEXT_IN_HIDDEN (Apple)");
              state.isPlaying = false;
              return; // 交给 WebAudio 分支继续
            }
          } catch {}

          try {
            const hidden =
              typeof document !== "undefined" ? document.hidden : false;
            const hasGuard =
              typeof (globalThis as any).shouldBlockHowlerInAppleHidden ===
              "function";
            const guardDecision = hasGuard
              ? (globalThis as any).shouldBlockHowlerInAppleHidden()
              : "absent";
            console.warn(
              `[probe] COMPLETE_CB_CALL_PLAYNEXT hidden=${hidden} guard=${guardDecision}`
            );
          } catch {}

          // 直接事件驱动：不使用固定延迟
          if (this.currentRoomId === roomId && !state.pendingProductSwitch) {
            this.playNext(roomId);
          } else {
            console.log(`🎵 房间已切换或有待切换产品，停止播放 ${roomId}`);
            state.isPlaying = false;
          }
        } else {
          console.log(`🎵 房间${roomId}不再是当前房间，停止播放`);
          state.isPlaying = false;
        }
      } else {
        if (state.pendingProductSwitch) {
          console.log(`🎵 有待切换产品，停止继续播放队列中的音频`);
        } else {
          console.log(`🎵 所有音频播放完毕，停止播放`);
        }
        state.isPlaying = false;
      }
    });

    (state as any).howlerPlayer = player;
    console.log(`🎵 Howler播放器初始化完成`);

    // 确保其他房间的Howler播放器被设置为静音
    for (const [otherRoomId, otherState] of this.roomStates.entries()) {
      if (otherRoomId !== roomId && (otherState as any).howlerPlayer) {
        const otherPlayer = (otherState as any)
          .howlerPlayer as HowlerSeamlessPlayer;
        const otherStatus = otherPlayer.getStatus();
        console.log(
          `🎵 设置房间${otherRoomId}的Howler播放器为静音 (当前: ${otherStatus.muted})`
        );
        otherPlayer.setMuted(true);
      }
    }
  }

  const player = (state as any).howlerPlayer as HowlerSeamlessPlayer;

  // 设置音频状态
  const isCurrentRoom = this.currentRoomId === roomId;
  // 兼容 Hash 路由与 History 路由两种模式
  const path = window.location.pathname || "";
  const hash = window.location.hash || "";
  const isInLiveRoomDetail =
    (path.includes("/myLiveRoom") && path !== "/myLiveRoom") ||
    hash.includes("/myLiveRoom");

  if (isCurrentRoom && isInLiveRoomDetail && this.currentRoomId !== null) {
    player.setMuted(false);
    player.setVolume(1);
    console.log(`🔊 房间${roomId}设置有声播放`);
  } else {
    player.setMuted(true);
    player.setVolume(0);
    console.log(`🔇 房间${roomId}设置静音后台播放`);
  }

  // 检查是否是恢复播放（有音频时间进度）
  const currentItem = state.queue[state.currentIndex];

  // 🔧 关键修复：如果处于产品切换状态，清除可能来自旧产品复用trackId的音频进度
  const isProductSwitching = !!(
    state.pendingProductSwitch ||
    state.switchingProductId ||
    state.lastProductSwitchTime
  );
  if (isProductSwitching && currentItem) {
    // 清除可能导致中间播放的进度数据，确保新产品从头开始播放
    delete (currentItem as any).audioTimeProgress;
    delete (currentItem as any).audioCurrentTime;
    delete (currentItem as any).lastSavedProgress;
    console.log(
      `🔧 产品切换中，清除音频播放进度: "${currentItem.text?.substring(0, 30)}..." trackId=${(currentItem as any).id}`
    );
  }

  let hasAudioProgress =
    currentItem && (currentItem as any).audioTimeProgress > 0;
  // 🍎 交接后“下一条”不做进度恢复，避免空窗与误判（一次性标志）
  if ((state as any).handoffNoRestore) {
    hasAudioProgress = false;
    (state as any).handoffNoRestore = false;
    console.warn("[fix] HANDOFF_NO_RESTORE_NEXT (Apple)");
  }
  // 决策日志：AppleMode + 进度恢复标志
  try {
    const ua = navigator.userAgent || "";
    const isIOS =
      /iPhone|iPad|iPod/i.test(ua) ||
      (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
    const isSafari = /Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua);
    const cfg: any =
      (typeof getConfig === "function" ? getConfig() : {}) || ({} as any);
    const iosAllowed = cfg && cfg.IOSAllowBackgroundPlay === true;
    const appleMode = iosAllowed && (isIOS || isSafari);
    console.log(
      `[probe] UNIFIED_DECISION AppleMode=${appleMode} hasAudioProgress=${!!(currentItem && (currentItem as any).audioTimeProgress > 0)} effectiveHasAudioProgress=${hasAudioProgress}`
    );
  } catch {}

  if (hasAudioProgress) {
    // 恢复播放模式：检查播放器是否已有正确的音频
    const howlerStatus = player.getStatus();
    const isCorrectAudio =
      howlerStatus.currentItem && howlerStatus.currentItem.id === item.id;

    if (isCorrectAudio && howlerStatus.duration > 0) {
      // 使用保存的音频时间进度直接恢复
      const savedTimeProgress = (currentItem as any).audioTimeProgress;
      const seekTime = savedTimeProgress * howlerStatus.duration;

      console.log(`🎵 恢复音频时间进度:`, {
        audioId: item.id,
        audioText: item.text.substring(0, 50) + "...",
        savedTimeProgress: (savedTimeProgress * 100).toFixed(1) + "%",
        audioDuration: howlerStatus.duration.toFixed(2) + "s",
        seekTime: seekTime.toFixed(2) + "s",
        currentAudioTime: howlerStatus.currentTime.toFixed(2) + "s"
      });

      player.setMuted(false);
      player.setVolume(1);
      // 使用平滑恢复方法，减少卡顿
      (player as any).smoothResumeFromTime(seekTime);
      state.isPlaying = true;
      // 刷新 Media Session（恢复路径同样需要元数据与状态）
      try {
        this.setupMediaSession(item);
        if ("mediaSession" in navigator) {
          // @ts-ignore
          navigator.mediaSession.playbackState = "playing";
        }
      } catch {}
      return true;
    }
  }

  // ------- 🍎 苹果专用与通用路径分离 开始 -------
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
  const isSafari = /Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua);
  const cfg: any =
    (typeof getConfig === "function" ? getConfig() : {}) || ({} as any);
  const iosAllowed = cfg && cfg.IOSAllowBackgroundPlay === true;
  const appleMode = iosAllowed && (isIOS || isSafari);

  console.log(`🍎 播放配置检查:`, {
    isIOS,
    isSafari,
    config: cfg,
    IOSAllowBackgroundPlay: cfg?.IOSAllowBackgroundPlay,
    iosAllowed,
    appleMode
  });

  // 🍎 移除 WebAudio 优先，直接使用 Howler 的 HTML5 Audio 后台播放

  // 准备 HowlerItem
  const howlerItem: HowlerAudioItem = {
    id: item.id,
    text: item.text,
    audioUrl: item.audioUrl,
    voiceNo: item.voiceNo,
    productId: item.productId,
    commentId: item.commentId
  };

  if (appleMode) {
    // 苹果端：若房间被标记为 WA-only，则不使用 Howler 前台播放（统一 WA→WA）
    try {
      const g: any = globalThis as any;
      const useWAOnly = !!(
        g &&
        g.__appleUseWAOnlyRooms &&
        typeof g.__appleUseWAOnlyRooms.has === "function" &&
        g.__appleUseWAOnlyRooms.has(roomId)
      );
      if (useWAOnly) {
        console.warn("[apple] WA_ONLY active: skip Howler front playback");
        return true;
      }
    } catch {}
    console.log(
      `🍎 AppleMode: 准备播放/队列加入: "${item.text.substring(0, 30)}..."`
    );
    // 交接窗口可见性抑制：若处于可见交接(active)且当前条仍由 WA 播放，禁止 Howler 启播/恢复
    let visible = false,
      handoffActive = false;
    try {
      visible = typeof document !== "undefined" ? !document.hidden : true;
    } catch {}
    try {
      const g: any = (globalThis as any).__appleHandoffActiveRooms;
      handoffActive = !!(g && typeof g.has === "function" && g.has(roomId));
    } catch {}

    // 建立一个已入队ID集合，避免重复加入播放列表
    if (!(state as any).howlerEnqueuedIds) {
      (state as any).howlerEnqueuedIds = new Set<string>();
    }

    const playerStatus = (state as any).howlerPlayer.getStatus();

    // 若当前播放器已在播放该音频，则无需重复入队
    if (playerStatus?.currentItem && playerStatus.currentItem.id === item.id) {
      console.log("🍎 AppleMode: 当前音频已在播放，无需重复入队");
    } else {
      // 进度恢复场景：设置等待标志，避免自动抢播
      if (hasAudioProgress) {
        console.log(`🍎 AppleMode: 准备恢复进度，设置等待标志`);
        player.setWaitingForProgressRestore(true);
      }

      // 未入队则加入播放列表
      if (!(state as any).howlerEnqueuedIds.has(howlerItem.id)) {
        await player.addToPlaylist(howlerItem);
        (state as any).howlerEnqueuedIds.add(howlerItem.id);
      }

      // 提前设置 Media Session，确保控制中心尽早出现
      try {
        this.setupMediaSession(item);
        if ("mediaSession" in navigator) {
          // @ts-ignore
          navigator.mediaSession.playbackState = "playing";
        }
      } catch {}

      // 启动播放（仅当播放器未在播放）
      if (!playerStatus?.isPlaying && !hasAudioProgress) {
        // 可见交接窗口：禁止 Howler 前台抢播，等待 WA 完成后由交接推进
        if (visible && handoffActive) {
          console.warn("[fix] VISIBLE_HANDOFF_SUPPRESS_HOWLER_START (Apple)");
          return true;
        }
        // 若存在预热音频，先停止并卸载
        try {
          const pre = (state as any).prewarmHowl as Howl | undefined;
          if (pre) {
            try {
              pre.stop();
              pre.unload();
            } catch {}
            (state as any).prewarmHowl = null;
          }
        } catch {}
        // 尝试提前解锁音频上下文，降低 iOS 首播失败概率
        try {
          (HowlerSeamlessPlayer as any).unlock &&
            (HowlerSeamlessPlayer as any).unlock();
        } catch {}
        try {
          const hidden =
            typeof document !== "undefined" ? document.hidden : false;
          const hasGuard =
            typeof (globalThis as any).shouldBlockHowlerInAppleHidden ===
            "function";
          const guardDecision = hasGuard
            ? (globalThis as any).shouldBlockHowlerInAppleHidden()
            : "absent";
          console.warn(
            `[probe] APPLEMODE_PLAYER_PLAY_REQUEST hidden=${hidden} guard=${guardDecision}`
          );
          // 修复：隐藏态下 Apple 设备由 WebAudio 接管，AppleMode 不再直接调用 Howler play()
          const ua = navigator.userAgent || "";
          const isIOS =
            /iPhone|iPad|iPod/i.test(ua) ||
            (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
          const isSafari =
            /Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua);
          if (hidden && (isIOS || isSafari)) {
            console.warn("[fix] APPLEMODE_SKIP_PLAY_IN_HIDDEN (Apple)");
            // 不调用 player.play()，由隐藏态 WebAudio 调度继续
            return true;
          }
        } catch {}
        player.play();
        state.isPlaying = true;
      }
    }

    // 设置媒体会话（重复设置是幂等的，确保控制中心可见）
    try {
      this.setupMediaSession(item);
      if ("mediaSession" in navigator) {
        // @ts-ignore
        navigator.mediaSession.playbackState = "playing";
        console.log("🍎 Media Session 已设置为播放状态");
        // @ts-ignore
        if (!navigator.mediaSession.__handlersBound) {
          // @ts-ignore
          navigator.mediaSession.setActionHandler &&
            navigator.mediaSession.setActionHandler("play", () => {
              try {
                player.play();
                /* @ts-ignore */ navigator.mediaSession.playbackState =
                  "playing";
              } catch {}
            });
          // @ts-ignore
          navigator.mediaSession.setActionHandler &&
            navigator.mediaSession.setActionHandler("pause", () => {
              try {
                player.pause();
                /* @ts-ignore */ navigator.mediaSession.playbackState =
                  "paused";
              } catch {}
            });
          // @ts-ignore
          navigator.mediaSession.setActionHandler &&
            navigator.mediaSession.setActionHandler("nexttrack", () => {
              try {
                this.advanceToNext(roomId);
                /* @ts-ignore */ navigator.mediaSession.playbackState =
                  "playing";
              } catch {}
            });
          // @ts-ignore
          navigator.mediaSession.__handlersBound = true;
        }
      }
    } catch {}
  } else {
    // 非苹果端：采用保留播放列表 + 预加载下一条，减少切换空隙
    console.log(`🎵 加载新音频: "${item.text.substring(0, 30)}..."`);

    // 🔧 关键修复：检查是否已经在等待恢复状态，避免重复设置导致循环等待
    const currentWaitingStatus =
      player.isWaitingForProgressRestore?.() || false;
    if (hasAudioProgress && !currentWaitingStatus) {
      console.log(`🎵 准备恢复进度，设置等待标志防止自动播放`);
      player.setWaitingForProgressRestore(true);
    } else if (hasAudioProgress && currentWaitingStatus) {
      console.log(`🎵 已在等待恢复状态，跳过重复设置等待标志`);
    }

    // 🔧 关键修复：检查播放器播放列表是否为空，如果为空则需要重新填充
    const playerStatus = player.getStatus();
    const playlistEmpty =
      !playerStatus.playlist || playerStatus.playlist.length === 0;

    // 避免重复入队：若已入队且播放列表不为空则不重复添加
    if (!(state as any).howlerEnqueuedIds) {
      (state as any).howlerEnqueuedIds = new Set<string>();
    }

    const alreadyEnqueued = (state as any).howlerEnqueuedIds.has(howlerItem.id);

    if (!alreadyEnqueued || playlistEmpty) {
      if (playlistEmpty && alreadyEnqueued) {
        console.log("🔧 播放器播放列表为空，重新添加音频到播放列表");
      }
      await player.addToPlaylist(howlerItem);
      (state as any).howlerEnqueuedIds.add(howlerItem.id);
    } else {
      console.log("🎵 当前音频已在Howler队列，无需重复添加");
    }

    if (hasAudioProgress) {
      console.log(`🎵 等待进度恢复，跳过立即播放`);
    } else {
      // 若存在预热音频，先停止并卸载
      try {
        const pre = (state as any).prewarmHowl as Howl | undefined;
        if (pre) {
          try {
            pre.stop();
            pre.unload();
          } catch {}
          (state as any).prewarmHowl = null;
        }
      } catch {}
      player.play();
      state.isPlaying = true;
      try {
        this.setupMediaSession(item);
        if ("mediaSession" in navigator) {
          // @ts-ignore
          navigator.mediaSession.playbackState = "playing";
          // @ts-ignore
          if (!navigator.mediaSession.__handlersBound) {
            // @ts-ignore
            navigator.mediaSession.setActionHandler &&
              navigator.mediaSession.setActionHandler("play", () => {
                try {
                  player.play();
                  /* @ts-ignore */ navigator.mediaSession.playbackState =
                    "playing";
                } catch {}
              });
            // @ts-ignore
            navigator.mediaSession.setActionHandler &&
              navigator.mediaSession.setActionHandler("pause", () => {
                try {
                  player.pause();
                  /* @ts-ignore */ navigator.mediaSession.playbackState =
                    "paused";
                } catch {}
              });
            // @ts-ignore
            navigator.mediaSession.setActionHandler &&
              navigator.mediaSession.setActionHandler("nexttrack", () => {
                try {
                  this.advanceToNext(roomId);
                  /* @ts-ignore */ navigator.mediaSession.playbackState =
                    "playing";
                } catch {}
              });
            // @ts-ignore
            navigator.mediaSession.setActionHandler &&
              navigator.mediaSession.setActionHandler("previoustrack", () => {
                // 这里简单处理：回到当前或不处理
              });
            // @ts-ignore
            navigator.mediaSession.__handlersBound = true;
          }
        }
      } catch {}
    }
  }
  // ------- 🍎 苹果专用与通用路径分离 结束 -------

  // 如果有音频时间进度，等待音频加载后恢复
  if (hasAudioProgress) {
    // 可见交接窗口：禁止对当前条做 Howler 前台进度恢复，等待 WA 自然结束后交接到下一条
    try {
      const visible = typeof document !== "undefined" ? !document.hidden : true;
      const g: any = (globalThis as any).__appleHandoffActiveRooms;
      const handoffActive = !!(
        g &&
        typeof g.has === "function" &&
        g.has(roomId)
      );
      if (appleMode && visible && handoffActive) {
        console.warn("[fix] VISIBLE_HANDOFF_SKIP_PROGRESS_RESTORE (Apple)");
        // 不做任何恢复操作，交接完成将直接播放下一条
        return true;
      }
    } catch {}
    // 防止 100% 恢复导致瞬间完成：做一次轻度夹逼
    const rawProg = Number((currentItem as any).audioTimeProgress || 0);
    const savedTimeProgress = Math.min(0.97, Math.max(0, rawProg));
    console.log(
      `🎵 新加载音频，准备恢复音频时间进度: ${(savedTimeProgress * 100).toFixed(1)}%`
    );

    // 等待标志已经在上面设置了，这里不需要重复设置

    // 等待音频加载完成以获取总时长
    const checkDurationAndSeek = () => {
      const howlerStatus = player.getStatus();
      if (howlerStatus.duration > 0) {
        // 二次保护：若进度仍然接近100%，在此再夹逼一次
        const clampedProgress = Math.min(0.97, Math.max(0, savedTimeProgress));
        const seekTime = clampedProgress * howlerStatus.duration;
        console.log(
          `🎵 恢复音频时间进度: 从 ${seekTime.toFixed(2)}s 开始播放 (总时长: ${howlerStatus.duration.toFixed(2)}s)`
        );

        // 关键修复：先恢复进度，再清除等待标志
        player.playFromTime(seekTime);

        // 设置播放状态
        state.isPlaying = true;

        // 延迟清除等待标志，确保播放已开始
        setTimeout(() => {
          player.setWaitingForProgressRestore(false);
          console.log(`🎵 进度恢复完成，清除等待标志，允许后续自动播放`);
        }, 100);
      } else {
        // 如果音频还没加载完成，等待一下再试（降低等待间隔以缩短空白）
        setTimeout(checkDurationAndSeek, 50);
      }
    };

    // 尽快执行一次检查，确保快速恢复（必要时再以较短间隔重试）
    setTimeout(checkDurationAndSeek, 50);

    // 安全机制：设置超时清除等待标志，防止永远卡住
    setTimeout(async () => {
      if (player.getStatus().waitingForProgressRestore) {
        console.warn(`🎵 超时清除等待标志，防止音频播放卡住`);
        player.setWaitingForProgressRestore(false);

        // 🔧 关键修复：清除等待标志后，检查并恢复播放器播放列表
        try {
          const roomState = this.roomStates.get(roomId);
          if (roomState && !roomState.isPlaying && roomState.queue.length > 0) {
            console.log(
              `🔄 超时清除后检查播放器状态: 队列长度${roomState.queue.length}, 当前索引${roomState.currentIndex}`
            );

            // 检查播放器播放列表是否为空
            const playerStatus = player.getStatus();
            const playlistEmpty =
              !playerStatus.playlist || playerStatus.playlist.length === 0;

            if (playlistEmpty) {
              console.log(`🔧 播放器播放列表为空，重新填充当前音频`);
              const currentItem = roomState.queue[roomState.currentIndex];
              if (currentItem) {
                const howlerItem = {
                  id: currentItem.id,
                  src: currentItem.audioUrl,
                  text: currentItem.text,
                  voiceNo: currentItem.voiceNo,
                  productId: currentItem.productId,
                  commentId: currentItem.commentId,
                  onEnd: () => this.onAudioComplete(roomId, currentItem.id),
                  onPlay: () => this.onAudioStart(roomId, currentItem.id),
                  onProgress: (progress: number) =>
                    this.onAudioProgress(roomId, currentItem.id, progress)
                };

                await player.addToPlaylist(howlerItem);
                // 重置入队标记，允许重新添加
                if ((roomState as any).howlerEnqueuedIds) {
                  (roomState as any).howlerEnqueuedIds.clear();
                  (roomState as any).howlerEnqueuedIds.add(currentItem.id);
                }
                console.log(
                  `🔧 已重新添加当前音频到播放列表: "${currentItem.text.substring(0, 30)}..."`
                );
              }
            }

            // 恢复播放并设置正确的进度
            try {
              const updatedPlayerStatus = player.getStatus();
              if (!updatedPlayerStatus.isPlaying) {
                // 🔧 关键修复：恢复到保存的进度而不是从头播放
                const currentItem = roomState.queue[roomState.currentIndex];
                if (currentItem && (currentItem as any).audioTimeProgress) {
                  const savedTimeProgress = Math.min(
                    0.97,
                    Math.max(
                      0,
                      Number((currentItem as any).audioTimeProgress || 0)
                    )
                  );
                  if (updatedPlayerStatus.duration > 0) {
                    const seekTime =
                      savedTimeProgress * updatedPlayerStatus.duration;
                    console.log(
                      `🔄 超时恢复：从进度 ${(savedTimeProgress * 100).toFixed(1)}% (${seekTime.toFixed(2)}s) 开始播放`
                    );
                    player.playFromTime(seekTime);
                  } else {
                    console.log(`🔄 超时恢复：音频未加载完成，使用普通播放`);
                    player.play();
                  }
                } else {
                  console.log(`🔄 超时恢复：无保存进度，从头播放`);
                  player.play();
                }
                roomState.isPlaying = true;
                console.log(`🔄 超时恢复播放成功，已调用播放器播放`);
              } else {
                console.log(`🔄 播放器已在播放中，无需重复调用`);
              }
            } catch (playError) {
              console.error(`🔧 直接调用播放器播放失败:`, playError);
              // 如果直接播放失败，再尝试完整的playNext流程
              setTimeout(() => {
                this.playNext(roomId);
              }, 100);
            }
          }
        } catch (error) {
          console.error(`🔧 超时清除后触发恢复播放失败:`, error);
        }
      }
    }, 5000); // 5秒超时
  }

  // 简化逻辑：每次只播放一个音频，播放完成后通过回调继续下一个
  console.log(`🎵 单曲播放模式: "${item.text.substring(0, 30)}..."`);

  return true;
}

// ====== 🍎 首播预热：提前建立 Now Playing 并播放极低音量的短引导音频 ======

// 构造一个短促可闻的正弦波 WAV（默认 0.5s@440Hz@22.05kHz@16bit）
function makeBeepWavDataUri(
  durationMs = 500,
  freqHz = 440,
  sampleRate = 22050,
  volume = 0.2
) {
  const samples = Math.floor(sampleRate * (durationMs / 1000));
  const numChannels = 1;
  const bitsPerSample = 16;
  const blockAlign = (numChannels * bitsPerSample) >> 3;
  const byteRate = sampleRate * blockAlign;
  const dataSize = samples * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  let offset = 0;
  const writeString = (s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset++, s.charCodeAt(i));
  };
  const writeUint32 = (d: number) => {
    view.setUint32(offset, d, true);
    offset += 4;
  };
  const writeUint16 = (d: number) => {
    view.setUint16(offset, d, true);
    offset += 2;
  };
  // RIFF header
  writeString("RIFF");
  writeUint32(36 + dataSize);
  writeString("WAVE");
  // fmt chunk
  writeString("fmt ");
  writeUint32(16);
  writeUint16(1); // PCM
  writeUint16(numChannels);
  writeUint32(sampleRate);
  writeUint32(byteRate);
  writeUint16(blockAlign);
  writeUint16(bitsPerSample);
  // data chunk
  writeString("data");
  writeUint32(dataSize);
  // samples
  for (let i = 0; i < samples; i++) {
    const t = i / sampleRate;
    const s = Math.sin(2 * Math.PI * freqHz * t);
    const amp = Math.max(-1, Math.min(1, s)) * volume;
    const val = Math.floor(amp * 32767);
    view.setInt16(offset, val, true);
    offset += 2;
  }
  const u8 = new Uint8Array(buffer);
  // 转 base64
  let bin = "";
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  const b64 = btoa(bin);
  return `data:audio/wav;base64,${b64}`;
}

export function prewarmNowPlayingForApple(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  try {
    if (!roomId || !this.roomStates.has(roomId)) return;
    const state = this.roomStates.get(roomId);
    const cfg: any =
      (typeof getConfig === "function" ? getConfig() : {}) || ({} as any);
    const iosAllowed = cfg && cfg.IOSAllowBackgroundPlay === true;
    const ua = navigator.userAgent || "";
    const isIOS =
      /iPhone|iPad|iPod/i.test(ua) ||
      (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
    const isSafari = /Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua);
    const appleMode = iosAllowed && (isIOS || isSafari);
    if (!appleMode) return;

    // 已预热则跳过
    if ((state as any).prewarmHowl) return;

    // 先设置占位 Media Session 信息
    try {
      const placeholder = {
        text: `${state.productList?.name || "直播"} · 正在准备...`
      } as any;
      this.setupMediaSession(placeholder);
      if ("mediaSession" in navigator) {
        // @ts-ignore
        navigator.mediaSession.playbackState = "playing";
        // 若支持，设置一个可见的 positionState（有助于部分系统显示）
        try {
          // @ts-ignore
          navigator.mediaSession.setPositionState &&
            navigator.mediaSession.setPositionState({
              duration: 1,
              playbackRate: 1,
              position: 0
            });
        } catch {}
      }
    } catch {}

    // 播放极低音量的短“可闻”引导音，触发 Now Playing
    const src = makeBeepWavDataUri(500, 440, 22050, 0.2);
    const h = new Howl({
      src: [src],
      html5: true,
      preload: true,
      loop: false,
      volume: 0.05,
      format: ["wav"],
      onload: () => {
        try {
          const node = (h as any)?._sounds?.[0]?._node as HTMLAudioElement;
          if (node) {
            node.setAttribute("playsinline", "true");
            node.setAttribute("webkit-playsinline", "true");
            node.preload = "auto";
            // 某些系统只有在可见播放进度时才展示，尝试强制加载足够帧
          }
        } catch {}
      },
      onplay: () => {
        // 记录预热开始
        (state as any).prewarmStartedAt = Date.now();
        try {
          if ("mediaSession" in navigator) {
            // @ts-ignore
            navigator.mediaSession.playbackState = "playing";
          }
        } catch {}
      },
      onend: () => {
        // 预热音频自然结束后保留 Now Playing，不立即卸载（等真实音频接管或超时清理）
      },
      onplayerror: () => {
        try {
          h.stop();
          h.unload();
        } catch {}
        (state as any).prewarmHowl = null;
      },
      onloaderror: () => {
        try {
          h.stop();
          h.unload();
        } catch {}
        (state as any).prewarmHowl = null;
      }
    });

    (state as any).prewarmHowl = h;
    try {
      h.play();
    } catch {}

    // 保险清理：若 6 秒内未被真实音频接管则停止预热
    setTimeout(() => {
      const pre = (state as any).prewarmHowl as Howl | undefined;
      if (pre) {
        try {
          pre.stop();
          pre.unload();
        } catch {}
        (state as any).prewarmHowl = null;
      }
    }, 6000);
  } catch {}
}

// 将新到达的音频提前加入 Howler 播放列表（苹果专用，避免后台新起播）
export async function addToHowlerPlaylist(
  this: RootState & { [key: string]: any },
  roomId: string,
  legacyItem: {
    id: string;
    text: string;
    audioUrl: string;
    voiceNo?: number;
    productId?: string | null;
  }
) {
  // 原本仅在 Apple 设备上预先入队，为减少首尾空隙，这里扩展到所有浏览器。
  // 对于非 Apple 端也进行预加载，交由播放器的重叠/淡入淡出来实现更平滑的衔接。

  if (!roomId || !this.roomStates.has(roomId)) return;
  const state = this.roomStates.get(roomId);
  if (!state) return;

  // 检查是否使用 UniversalWebAudio
  const g: any = globalThis as any;
  const useUniversalWebAudio = !!(
    g &&
    g.__universalWebAudioRooms &&
    typeof g.__universalWebAudioRooms.has === "function" &&
    g.__universalWebAudioRooms.has(roomId)
  );

  if (useUniversalWebAudio) {
    await feedUniversalWebAudio.call(this as any, roomId, legacyItem);
    return;
  }

  // 🍎 直接使用 Howler 播放器，移除 WebAudio
  let player = (state as any).howlerPlayer as HowlerSeamlessPlayer | undefined;
  if (!player) {
    try {
      player = new HowlerSeamlessPlayer(roomId);
      // 预加载用途：静音并设置实例音量为0，避免任何可闻输出
      (player as any).setMuted && (player as any).setMuted(true);
      (player as any).setVolume && (player as any).setVolume(0);
      (state as any).howlerPlayer = player;
      console.log(`📱 Howler: 创建预加载播放器 room=${roomId}`);
    } catch (e) {
      console.warn(`📱 Howler: 创建预加载播放器失败 room=${roomId}`, e);
      return;
    }
  }

  if (!(state as any).howlerEnqueuedIds) {
    (state as any).howlerEnqueuedIds = new Set<string>();
  }

  // 已加入则跳过
  if ((state as any).howlerEnqueuedIds.has(legacyItem.id)) return;

  const howlerItem: HowlerAudioItem = {
    id: legacyItem.id,
    text: legacyItem.text,
    audioUrl: legacyItem.audioUrl,
    voiceNo: (legacyItem as any).voiceNo,
    productId: (legacyItem as any).productId ?? null,
    commentId: (legacyItem as any).commentId ?? null
  };

  try {
    // 紧急：直接插入到当前之后（LIFO），确保下一句优先
    if (
      Number(howlerItem.voiceNo) === -1 &&
      typeof (player as any).addUrgentNext === "function"
    ) {
      (player as any).addUrgentNext(howlerItem);
    } else {
      // 普通：预加载加入，不触发 play()，让重叠机制接管
      player.addToPlaylist(howlerItem);
    }
    (state as any).howlerEnqueuedIds.add(legacyItem.id);
    console.log(
      `📱 Howler: 预先加入队列 -> ${legacyItem.text.substring(0, 30)}...`
    );
    // 若播放器空闲且有可播内容，尝试立即启动，避免末尾长时间无声
    try {
      (player as any).startIfIdle && (player as any).startIfIdle();
    } catch {}
  } catch (e) {
    console.warn("📱 Howler: 预加入失败，忽略", e);
  }
}

// 处理音频错误
export function onPlayError(
  this: RootState & { [key: string]: any },
  roomId: string,
  _error: any = null
) {
  if (!roomId || !this.roomStates.has(roomId)) {
    console.warn(`🎵 错误处理: 房间${roomId}不存在`);
    return;
  }

  const state = this.roomStates.get(roomId);
  console.error(
    `🎵 音频播放错误: 房间${roomId}, 当前索引${state.currentIndex}`
  );

  // 清理当前播放器状态
  if ((state as any).howlerPlayer) {
    try {
      const howlerPlayer = (state as any).howlerPlayer;
      howlerPlayer.stop();
    } catch {
      // 忽略停止错误
    }
  }

  // 标记当前音频为已播放（错误处理）
  const currentItem = state.queue[state.currentIndex];
  if (currentItem) {
    currentItem.status = "played";
    currentItem.playedCharCount = currentItem.text.length;
    console.warn(
      `🎵 标记错误音频为已播放: "${currentItem.text.substring(0, 30)}..."`
    );
  }

  // 尝试播放下一个音频
  const nextIndex = state.currentIndex + 1;
  if (nextIndex < state.queue.length) {
    state.currentIndex = nextIndex;
    console.warn(
      `🎵 错误恢复: 尝试播放下一个音频 (${nextIndex}/${state.queue.length})`
    );

    // 增加延迟避免连续错误
    setTimeout(() => {
      this.playNext(roomId);
    }, 1000);
  } else {
    console.warn(`🎵 播放队列已结束，停止播放`);
    state.isPlaying = false;
  }
}

// 切换到下一个音频
export function advanceToNext(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  const state = this.roomStates.get(roomId);
  if (!state) return;

  // 标记当前音频为已完成
  const currentItem = state.queue[state.currentIndex];
  if (currentItem) {
    const newQueue = [...state.queue];
    newQueue[state.currentIndex] = {
      ...currentItem,
      playedCharCount: currentItem.text.length,
      status: "played"
    };
    state.queue = newQueue;
  }

  // 检查下一个音频
  const nextIndex = state.currentIndex + 1;
  if (nextIndex < state.queue.length) {
    state.currentIndex++;
    this.playNext(roomId);
  } else {
    state.isPlaying = false;
  }
}

// 恢复房间播放器 - 组件重新附着时调用
export function resumeRoom(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  if (!roomId || !this.roomStates.has(roomId)) {
    console.warn(`🎵 恢复房间播放器: 房间${roomId}不存在`);
    return false;
  }

  const state = this.roomStates.get(roomId);
  const howlerPlayer = (state as any).howlerPlayer;

  if (!howlerPlayer) {
    console.warn(`🎵 恢复房间播放器: 房间${roomId}没有音频播放器`);
    return false;
  }

  try {
    // 🔧 清除等待恢复标志
    (state as any).waitingForRestore = false;
    (state as any).readyToRestore = false;

    // 🔧 清除播放器实例的等待恢复标志
    try {
      if (typeof howlerPlayer.setWaitingForProgressRestore === "function") {
        howlerPlayer.setWaitingForProgressRestore(false);
      }
    } catch (e) {
      console.warn(`清除播放器等待恢复标志失败:`, e);
    }

    // 如果是当前活跃房间，取消静音
    if (this.currentRoomId === roomId) {
      howlerPlayer.setMuted(false);
      howlerPlayer.setVolume(1);
      console.log(`🎵 房间${roomId}播放器已恢复音量 (当前房间)`);
    } else {
      // 非当前房间保持静音
      howlerPlayer.setMuted(true);
      howlerPlayer.setVolume(0);
      console.log(`🎵 房间${roomId}播放器保持静音 (非当前房间)`);
    }

    // 更新最后活动时间
    (state as any).lastActiveTime = Date.now();

    return true;
  } catch (error) {
    console.warn(`🎵 恢复房间${roomId}播放器时出错:`, error);
    return false;
  }
}

// 暂停房间 - 组件级别保留播放器
export function pauseRoom(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  return this.leaveRoom(roomId, { keepPlayback: true });
}

// 离开房间
export function leaveRoom(
  this: RootState & { [key: string]: any },
  roomId: string,
  options?: {
    keepPlayback?: boolean;
    keepConnection?: boolean;
  }
) {
  if (!roomId || !this.roomStates.has(roomId)) {
    console.warn(`🎵 离开房间: 房间${roomId}不存在`);
    return;
  }

  const state = this.roomStates.get(roomId);
  console.log(`🎵 离开房间: ${roomId}, 当前房间: ${this.currentRoomId}`);

  // 保存当前播放状态快照
  const currentSnapshot = {
    currentIndex: state.currentIndex,
    isPlaying: state.isPlaying,
    currentItem: state.queue[state.currentIndex]
  };
  console.log(`🎵 离开时状态快照:`, {
    currentIndex: currentSnapshot.currentIndex,
    totalItems: state.queue.length,
    audioText: currentSnapshot.currentItem?.text?.substring(0, 50) + "...",
    playedCharCount: currentSnapshot.currentItem?.playedCharCount || 0,
    isPlaying: currentSnapshot.isPlaying
  });

  // 保存Howler播放器进度并暂停
  if ((state as any).howlerPlayer) {
    try {
      const howlerPlayer = (state as any).howlerPlayer;

      // 离开前保存当前播放进度
      const howlerStatus = howlerPlayer.getStatus();
      if (howlerStatus.isPlaying && state.currentIndex < state.queue.length) {
        const currentItem = state.queue[state.currentIndex];
        if (currentItem && howlerStatus.duration > 0) {
          // 保存音频时间进度而不是估算文字进度
          const timeProgress = howlerStatus.currentTime / howlerStatus.duration;

          // 保存音频时间进度到专门的字段
          (currentItem as any).audioTimeProgress = timeProgress;
          (currentItem as any).audioCurrentTime = howlerStatus.currentTime;
          (currentItem as any).audioDuration = howlerStatus.duration;

          console.log(
            `🎵 保存音频时间进度: "${currentItem.text.substring(0, 30)}..." -> ${howlerStatus.currentTime.toFixed(2)}s/${howlerStatus.duration.toFixed(2)}s (${(timeProgress * 100).toFixed(1)}%)`
          );
        }
      }

      // 保存状态到房间数据，防止回调干扰
      (state as any).lastValidState = {
        currentIndex: state.currentIndex,
        currentItem: state.queue[state.currentIndex],
        audioTimeProgress:
          (state.queue[state.currentIndex] as any)?.audioTimeProgress || 0,
        audioCurrentTime:
          (state.queue[state.currentIndex] as any)?.audioCurrentTime || 0
      };

      // 根据options决定如何处理播放器
      const keepPlayback = options?.keepPlayback || false;

      if (keepPlayback) {
        // 保持播放器继续运行
        try {
          howlerPlayer.setMuted(false);
        } catch {}
        (state as any).lastActiveTime = Date.now();
        // 🔧 新增：设置等待恢复标志，防止WebSocket重连时销毁播放器
        (state as any).waitingForRestore = true;
        (state as any).readyToRestore = true;
        // 🔧 关键修复：同时设置播放器实例的等待恢复标志
        try {
          if (typeof howlerPlayer.setWaitingForProgressRestore === "function") {
            howlerPlayer.setWaitingForProgressRestore(true);
          }
        } catch (e) {
          console.warn(`设置播放器等待恢复标志失败:`, e);
        }
        console.log(
          `🎵 房间${roomId}保持播放，等待UI重新附着 (当前播放状态: ${howlerPlayer.getStatus().isPlaying})`
        );
      } else {
        // 修改：暂停并静音播放器，保持资源以便快速恢复
        howlerPlayer.setMuted(true);
        howlerPlayer.pause();
        // 记录最后活动时间，用于智能资源清理
        (state as any).lastActiveTime = Date.now();
        console.log(`🎵 房间${roomId}播放器已暂停并静音(保持资源)`);
      }
    } catch {
      console.warn(`🎵 处理房间${roomId}播放器时出现错误`);
    }
  }

  // 根据options决定是否关闭WebSocket连接
  const keepConnection = options?.keepConnection || false;

  if (!keepConnection) {
    // 关键修复：关闭 WebSocket 连接
    try {
      console.log(`🔌 关闭房间${roomId}的WebSocket连接`);
      this.leaveRoomWebSocket(roomId);
    } catch (error) {
      console.warn(`🔌 关闭WebSocket时出错:`, error);
    }
  } else {
    console.log(`🔌 房间${roomId}保持WebSocket连接`);
  }

  // 🍎 清理苹果端 HTML5 链式播放器（如有）
  try {
    const st = this.roomStates.get(roomId);
    if (st && (st as any).__appleHtml5Chain__) {
      (this as any).destroyAppleHtml5Chain &&
        (this as any).destroyAppleHtml5Chain(roomId);
      (st as any).__appleHtml5Chain__ = null;
    }
  } catch {}

  // 关键修复：只在是当前房间时才清空 currentRoomId
  if (this.currentRoomId === roomId) {
    this.currentRoomId = null;
    console.log(`🎵 清空当前房间ID`);
  }

  // 不直接设置 isPlaying = false，保持播放状态以便恢复
  console.log(`🎵 房间${roomId}离开完成，播放状态保持: ${state.isPlaying}`);
}

// 进入房间
export function enterRoom(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  if (!roomId) return;

  // console.log(`🎵 进入房间: ${roomId}, 当前房间: ${this.currentRoomId}`);

  if (!this.roomStates.has(roomId)) {
    const savedState = this.loadStateFromStorage();
    if (!savedState || !this.roomStates.has(roomId)) {
      console.warn(`🎵 房间${roomId}不存在，无法进入`);
      return;
    }
  }

  const state = this.roomStates.get(roomId);
  // 关键修复：检查直播状态，如果是已结束状态则清理相关数据
  if (state.productList?.status === "ended") {
    console.log(`检测到房间${roomId}直播已结束，清理相关状态`);
    // 清理播放状态
    state.isPlaying = false;
    state.isPreloading = false;
    // 旧播放器属性已移除，使用 Howler.js 统一管理
    state.readyToRestore = false;

    // 清理队列和消息
    state.queue = [];
    state.currentIndex = 0;
    state.publicMessages = [];

    // 清理播放器
    if ((state as any).howlerPlayer) {
      try {
        console.log(
          `🔍 [DESTROY_TRACE] enterRoom检测到直播已结束，销毁播放器 - 调用栈:`,
          new Error().stack
        );
        const howlerPlayer = (state as any).howlerPlayer;
        howlerPlayer.stop();
        howlerPlayer.destroy();
        (state as any).howlerPlayer = null;
      } catch {
        (state as any).howlerPlayer = null;
      }
    }

    // 保存清理后的状态
    this.saveStateToStorage();
    console.log(`房间${roomId}已结束直播状态清理完成`);
  }

  // 关键修复：先停止其他房间的播放并断开WebSocket连接，再切换到当前房间
  for (const [otherRoomId, otherState] of this.roomStates.entries()) {
    if (otherRoomId !== roomId) {
      // console.log(`🎵 停止其他房间${otherRoomId}的所有功能`);

      // 停止其他房间的播放
      otherState.isPlaying = false;

      if ((otherState as any).howlerPlayer) {
        const otherPlayer = (otherState as any)
          .howlerPlayer as HowlerSeamlessPlayer;
        // console.log(`🎵 完全停止房间${otherRoomId}的播放器`);
        otherPlayer.setMuted(true);
        otherPlayer.setVolume(0);
        otherPlayer.stop(); // 完全停止其他房间的播放
        otherPlayer.destroy(); // 销毁播放器实例
        (otherState as any).howlerPlayer = null;
      }

      // 关键修复：断开其他房间的WebSocket连接
      try {
        // console.log(`🔌 断开房间${otherRoomId}的WebSocket连接`);
        this.leaveRoomWebSocket(otherRoomId);
      } catch (error) {
        // console.warn(`🔌 断开房间${otherRoomId}WebSocket时出错:`, error);
      }
    }
  }

  // 设置当前房间
  this.currentRoomId = roomId;

  // 重新建立 WebSocket 连接
  try {
    // console.log(`🔌 重新建立房间${roomId}的WebSocket连接`);
    // 重新初始化公屏WebSocket
    if (state.productList?.status === "live") {
      if (state.roomLink) {
        this.bindCommentWebSocket();
      }
      // 重新初始化音频队列WebSocket
      this.initAudioQueueWebSocket(roomId);
    }
  } catch (error) {
    console.warn(`🔌 重建WebSocket连接时出错:`, error);
  }

  // 检查是否有保存的有效状态，防止回调干扰导致的索引错误
  if ((state as any).lastValidState) {
    const lastValid = (state as any).lastValidState;
    // console.log(`🎵 发现保存的有效状态:`, {
    //   savedIndex: lastValid.currentIndex,
    //   currentIndex: state.currentIndex,
    //   savedPlayedCharCount: lastValid.playedCharCount,
    //   currentPlayedCharCount:
    //     state.queue[state.currentIndex]?.playedCharCount || 0
    // });

    // 如果当前索引被意外推进，恢复到保存的状态
    if (
      lastValid.currentIndex < state.currentIndex &&
      lastValid.currentItem &&
      lastValid.audioTimeProgress < 0.95
    ) {
      // console.log(
      //   `🎵 检测到索引被意外推进，恢复到保存的状态: ${state.currentIndex} -> ${lastValid.currentIndex}`
      // );
      state.currentIndex = lastValid.currentIndex;

      // 恢复音频时间进度
      if (state.queue[state.currentIndex]) {
        (state.queue[state.currentIndex] as any).audioTimeProgress =
          lastValid.audioTimeProgress;
        (state.queue[state.currentIndex] as any).audioCurrentTime =
          lastValid.audioCurrentTime;
      }
    }

    // 清除保存的状态
    delete (state as any).lastValidState;
  }

  // 关键修复：检查播放器是否存在(现在用pause不会销毁)
  if ((state as any).howlerPlayer) {
    const player = (state as any).howlerPlayer as HowlerSeamlessPlayer;
    const howlerStatus = player.getStatus();

    // console.log(
    //   `🎵 房间${roomId}发现现存播放器，快速恢复 (当前: ${howlerStatus.currentIndex}/${howlerStatus.totalItems})`
    // );

    // 检查是否需要恢复正确的播放进度
    const currentItem = state.queue[state.currentIndex];
    const needsTimeRestore =
      currentItem && (currentItem as any).audioTimeProgress > 0;

    // 如果已经在播放且没有静音，可能不需要任何操作
    if (
      howlerStatus.isPlaying &&
      !howlerStatus.muted &&
      howlerStatus.volume > 0.8
    ) {
      // console.log(`🎵 播放器状态良好，无需额外操作`);
      return;
    }

    if (needsTimeRestore) {
      const savedTimeProgress = (currentItem as any).audioTimeProgress;
      const expectedTime = savedTimeProgress * howlerStatus.duration;
      const actualTime = howlerStatus.currentTime;
      const timeDiff = Math.abs(expectedTime - actualTime);

      if (timeDiff > 2) {
        // console.log(
        //   `🎵 需要恢复播放位置: 从${actualTime.toFixed(2)}s -> ${expectedTime.toFixed(2)}s (差异${timeDiff.toFixed(2)}s)`
        // );

        // 优化：使用平滑恢复方法，避免卡顿
        player.setMuted(false);
        player.setVolume(1);
        (player as any).smoothResumeFromTime(expectedTime);
      } else {
        // 时间差异较小，直接恢复播放即可
        // console.log(
        //   `🎵 播放位置基本正确(差异${timeDiff.toFixed(2)}s)，直接恢复播放`
        // );
        player.setMuted(false);
        player.setVolume(1);

        if (!howlerStatus.isPlaying) {
          player.resume();
        } else {
          console.log(`🎵 播放器已在运行，只需取消静音`);
        }
      }
    } else {
      // 没有保存的进度，直接恢复播放
      // console.log(`🎵 无保存进度，直接恢复播放状态`);
      player.setMuted(false);
      player.setVolume(1);

      if (!howlerStatus.isPlaying) {
        player.resume();
      }
    }

    return;
  }

  // 如果有队列数据且不在播放，才开始播放（去除95%近似判断，改由事件回调推进）
  if (state.queue.length > 0 && state.currentIndex < state.queue.length) {
    // 跳过不可播项：版本/产品/保护期时间/无URL
    const PROTECT_MS = Number((state as any).switchProtectMs ?? 5000);
    const inProtect =
      !!state.lastProductSwitchTime &&
      Date.now() - state.lastProductSwitchTime <= PROTECT_MS;
    while (state.currentIndex < state.queue.length) {
      const q = state.queue[state.currentIndex] as any;
      const currentPid =
        state.switchingProductId || state.selectedProductId || null;
      const sameProduct = !!(
        q?.productId &&
        currentPid &&
        q.productId === currentPid
      );
      const verMismatchRaw = q?.version !== (state.switchVersion ?? 0);
      const verMismatch = verMismatchRaw && !sameProduct;
      const wrongProduct =
        state.switchingProductId &&
        q?.productId &&
        q.productId !== state.switchingProductId;
      const tooOldInProtect =
        inProtect &&
        q?.createTime &&
        q.createTime < state.lastProductSwitchTime!;
      const noUrl = !q?.audioUrl;
      if (verMismatch || wrongProduct || tooOldInProtect || noUrl) {
        console.warn(
          `⏭️ 跳过不可播项1 idx=${state.currentIndex} verMis=${verMismatch} (raw=${verMismatchRaw}) wrongProduct=${!!wrongProduct} oldInProtect=${!!tooOldInProtect} noUrl=${!!noUrl} itemPid=${q?.productId || "NA"} curPid=${currentPid || "NA"} itemVer=${q?.version ?? "NA"} curVer=${state.switchVersion ?? 0}`
        );
        state.currentIndex++;
        continue;
      }
      break;
    }

    if (state.currentIndex >= state.queue.length) {
      state.isPlaying = false;
      console.log(`🎵 房间${roomId}所有音频已播放完毕（版本过滤后）`);
      return;
    }

    const currentItem = state.queue[state.currentIndex];

    // console.log(`🎵 房间${roomId}当前音频分析:`, {
    //   currentIndex: state.currentIndex,
    //   totalItems: state.queue.length,
    //   audioId: currentItem?.id,
    //   audioText: currentItem?.text?.substring(0, 50) + "...",
    //   playedCharCount: currentItem?.playedCharCount || 0,
    //   totalCharCount: currentItem?.text?.length || 0,
    //   status: currentItem?.status
    // });

    // 开始播放
    // console.log(
    //   `🎵 房间${roomId}开始播放，当前索引: ${state.currentIndex}/${state.queue.length}`
    // );

    // 显示当前要播放的音频信息
    const currentPlayItem = state.queue[state.currentIndex];
    // console.log(
    //   `🎵 准备播放: "${currentPlayItem.text.substring(0, 50)}..." 进度: ${currentPlayItem.playedCharCount || 0}/${currentPlayItem.text.length}`
    // );

    state.isPlaying = true;

    // 🍎 Apple/WA-only：不要在统一层再次触发 playNext，由 WA feed 接管，否则会重复起播/造成短暂卡顿
    try {
      const ua = navigator.userAgent || "";
      const appleLike =
        /iPhone|iPad|iPod/i.test(ua) ||
        (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1) ||
        (/Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua));
      const waOnly = (() => {
        try {
          const g: any = (globalThis as any).__appleUseWAOnlyRooms;
          return !!(g && typeof g.has === "function" && g.has(roomId));
        } catch {
          return false;
        }
      })();
      if (appleLike && waOnly) {
        console.log(
          `🍎 Apple/WA-only：跳过统一层延迟 playNext，由 WA 接管起播`
        );
        return;
      }
    } catch {}

    // 其他平台：根据播放器状态决定延迟时间
    const hasExistingPlayer = (state as any).howlerPlayer;
    const delayTime = hasExistingPlayer ? 50 : 200;
    console.log(
      `🎵 延迟${delayTime}ms后开始播放 (有播放器: ${hasExistingPlayer})`
    );
    setTimeout(() => {
      this.playNext(roomId);
    }, delayTime);
  } else {
    console.log(`🎵 房间${roomId}没有音频队列或播放完毕`);
  }
}

// 用户交互恢复播放
export async function resumePlayWithUserInteraction(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  if (!roomId || !this.roomStates.has(roomId)) return false;

  const state = this.roomStates.get(roomId);
  // 旧播放器属性已移除，使用 Howler.js 统一管理

  try {
    await this.playNext(roomId);
    return true;
  } catch {
    return false;
  }
}

// 清理所有音频资源
export function cleanupAllTimers(this: RootState & { [key: string]: any }) {
  this.roomStates.forEach((_state, _roomId) => {
    // 清理Howler播放器
    if ((_state as any).howlerPlayer) {
      try {
        const howlerPlayer = (_state as any).howlerPlayer;
        howlerPlayer.stop();
        howlerPlayer.destroy();
        (_state as any).howlerPlayer = null;
      } catch {
        (_state as any).howlerPlayer = null;
      }
    }
  });
}

// 媒体会话设置
export function setupMediaSession(
  this: RootState & { [key: string]: any },
  item: any
) {
  if ("mediaSession" in navigator) {
    try {
      const state = this.currentRoomState as any;
      const productName = state?.productList?.name;
      const speaker =
        state?.selectedVoice?.voiceName || state?.livePlatform || "AI播音";
      const baseTitle =
        item && item.text ? item.text.substring(0, 50) + "..." : "正在播放";
      const title = productName ? `${productName} · ${baseTitle}` : baseTitle;
      const artist = speaker;
      const album = productName || "智能直播";
      // 取封面：productDetail 常见字段兜底
      let cover: string | undefined;
      const pd = state?.productDetail || {};
      const keys = [
        "coverUrl",
        "imageUrl",
        "img",
        "image",
        "pic",
        "logo",
        "poster",
        "thumb",
        "thumbnail"
      ];
      for (const k of keys) {
        const v = pd?.[k];
        if (typeof v === "string" && v.length > 0) {
          cover = v;
          break;
        }
      }
      const artwork = cover
        ? [{ src: cover, sizes: "256x256", type: "image/png" }]
        : [
            { src: "/favicon.ico", sizes: "64x64", type: "image/x-icon" },
            { src: "/logo.png", sizes: "128x128", type: "image/png" }
          ];
      // @ts-ignore
      navigator.mediaSession.metadata = new MediaMetadata({
        title,
        artist,
        album,
        artwork
      });

      // 预加载封面以便及早发现不可达问题（仅日志用途）
      if (cover) {
        try {
          const img = new Image();
          // 尝试匿名加载，失败则仅记录日志，不打断播放
          img.crossOrigin = "anonymous";
          img.onload = () => {
            // 可选：成功也可记录一次
            console.log(`[media] artwork loaded: ${cover}`);
          };
          img.onerror = () => {
            console.warn(`[media] artwork load failed: ${cover}`);
            try {
              emitter.emit("mobileAudioLog", {
                level: "warn",
                source: "media-session",
                message: `封面加载失败: ${cover}`,
                timestamp: Date.now()
              });
            } catch {}
          };
          img.src = cover;
        } catch {}
      }
    } catch {}
  }
}

// 强制清理房间播放状态
export function forceCleanupRoom(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  if (!roomId || !this.roomStates.has(roomId)) return;

  const state = this.roomStates.get(roomId);
  console.log(`🎵 强制清理房间${roomId}的播放状态`);

  // 停止播放状态
  state.isPlaying = false;

  // 清理播放器
  if ((state as any).howlerPlayer) {
    try {
      console.log(
        `🔍 [DESTROY_TRACE] forceCleanupRoom销毁播放器 - 调用栈:`,
        new Error().stack
      );
      const howlerPlayer = (state as any).howlerPlayer;
      howlerPlayer.stop();
      howlerPlayer.destroy();
      (state as any).howlerPlayer = null;
      console.log(`🎵 房间${roomId}播放器已彻底清理`);
    } catch {
      (state as any).howlerPlayer = null;
    }
  }

  // 清理防抖时间戳
  if (state.lastLogTime) {
    const playKey = `${roomId}_play_next`;
    delete state.lastLogTime[playKey];
  }
}

// 新增：智能资源管理 - 清理长时间未使用的播放器
export function cleanupUnusedPlayers(
  this: RootState & { [key: string]: any },
  maxIdleTime: number = 5 * 60 * 1000 // 默认5分钟
) {
  const now = Date.now();
  const roomsToCleanup: string[] = [];

  console.log(`🧹 检查未使用的播放器资源...`);

  for (const [roomId, state] of this.roomStates.entries()) {
    // 如果是当前房间，跳过清理
    if (roomId === this.currentRoomId) continue;

    // 检查最后活动时间
    const lastActiveTime = (state as any).lastActiveTime || 0;
    const idleTime = now - lastActiveTime;

    if (idleTime > maxIdleTime && (state as any).howlerPlayer) {
      roomsToCleanup.push(roomId);
      console.log(
        `🧹 房间${roomId}闲置${Math.round(idleTime / 1000)}秒，标记清理`
      );
    }
  }

  // 清理闲置的播放器
  roomsToCleanup.forEach(roomId => {
    this.forceCleanupRoom(roomId);
  });

  if (roomsToCleanup.length > 0) {
    console.log(`🧹 已清理${roomsToCleanup.length}个闲置播放器`);
  }
}
